import { useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FileText, Download, Upload, FolderKanban, Trash2,
  AlertTriangle, Loader2, CheckCircle2, Clock, Tag, FileBox, Layers,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import {
  uploadProjectDeliverable, uploadMasterTemplate,
  getDeliverableSignedUrl, deleteDeliverableUpload,
} from "./deliverablesUpload";
import { format, parseISO, formatDistanceToNow } from "date-fns";

// =============================================================================
// ProjectDeliverablesScreen — /projects/:id/deliverables
// T-067 — Project Deliverables Library / MOP Templates.
//
// Three category tabs (Acceptance / Design / Delivery). Each tab renders
// lines for that category (filtered by project carrier — generic lines plus
// carrier-specific ones). Each line card has:
//   * Master template (if has_master): download + version + mgr+ re-upload
//   * Project uploads list: date, uploader, download, capturer-self/mgr+ delete
//   * "+ Upload new" button (visible to project team + mgr+)
// =============================================================================

interface ProjectMini {
  project_id:   string;
  project_code: string;
  project_name: string;
  carrier_id:   string | null;
  carrier?:     { carrier_id: string; legal_name: string; carrier_code: string } | null;
}

interface Category {
  category_id:  string;
  code:         string;
  display_name: string;
  description:  string | null;
  sort_order:   number;
}

interface Line {
  line_id:                     string;
  category_id:                 string;
  carrier_id:                  string | null;
  code:                        string;
  display_name:                string;
  description:                 string | null;
  has_master:                  boolean;
  master_storage_path:         string | null;
  master_version_label:        string | null;
  master_uploaded_at:          string | null;
  master_file_size_bytes:      number | null;
  master_file_name:            string | null;
  sort_order:                  number;
}

interface Upload {
  upload_id:               string;
  line_id:                 string;
  project_id:              string;
  storage_path:            string;
  file_name:               string;
  file_size_bytes:         number | null;
  mime_type:               string | null;
  version_label:           string | null;
  status:                  "draft" | "submitted" | "accepted" | "archived";
  notes:                   string | null;
  uploaded_at:             string;
  uploaded_by_employee_id: string | null;
  is_archived:             boolean;
}

const STATUS_TONE: Record<Upload["status"], "open" | "pending" | "approved" | "default"> = {
  draft:     "default",
  submitted: "pending",
  accepted:  "approved",
  archived:  "default",
};

// -----------------------------------------------------------------------------

export default function ProjectDeliverablesScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canManage = hasRole(user, "manager");

  const [activeTab, setActiveTab] = useState<string>("acceptance");

  // -- Load project (for carrier filtering) -------------------------------
  const { data: project } = useQuery({
    queryKey: ["deliv_project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(`project_id, project_code, project_name, carrier_id,
                 carrier:carrier_id(carrier_id, legal_name, carrier_code)`)
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as ProjectMini | null;
    },
  });

  // -- Load categories ----------------------------------------------------
  const { data: categories = [] } = useQuery({
    queryKey: ["deliv_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_library_categories")
        .select("category_id, code, display_name, description, sort_order")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as Category[];
    },
  });

  // -- Load lines for active category (filter by carrier) -----------------
  const activeCategoryId = useMemo(
    () => categories.find(c => c.code === activeTab)?.category_id ?? null,
    [categories, activeTab],
  );

  const { data: lines = [], isLoading: loadingLines } = useQuery({
    queryKey: ["deliv_lines", activeCategoryId, project?.carrier_id ?? null],
    enabled: !!activeCategoryId,
    queryFn: async () => {
      let q = supabase
        .from("document_library_lines")
        .select(`line_id, category_id, carrier_id, code, display_name,
                 description, has_master, master_storage_path,
                 master_version_label, master_uploaded_at,
                 master_file_size_bytes, master_file_name, sort_order`)
        .eq("category_id", activeCategoryId!)
        .eq("is_active", true)
        .order("sort_order");
      // Show generic lines + carrier-specific lines for this project's carrier
      if (project?.carrier_id) {
        q = q.or(`carrier_id.is.null,carrier_id.eq.${project.carrier_id}`);
      } else {
        q = q.is("carrier_id", null);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Line[];
    },
  });

  // -- Load uploads for this project across all lines ---------------------
  const { data: uploads = [], refetch: refetchUploads } = useQuery({
    queryKey: ["deliv_uploads", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_library_uploads")
        .select(`upload_id, line_id, project_id, storage_path, file_name,
                 file_size_bytes, mime_type, version_label, status, notes,
                 uploaded_at, uploaded_by_employee_id, is_archived`)
        .eq("project_id", projectId!)
        .eq("is_archived", false)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Upload[];
    },
  });

  const uploadsByLine = useMemo(() => {
    const m = new Map<string, Upload[]>();
    for (const u of uploads) {
      const arr = m.get(u.line_id) ?? [];
      arr.push(u);
      m.set(u.line_id, arr);
    }
    return m;
  }, [uploads]);

  if (!user) return null;
  if (!project) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Project not found"
            description="This project doesn't exist or you don't have access to it."
            icon={FolderKanban}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      {/* Back */}
      <Link
        to={`/projects/${projectId}`}
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
      >
        <ArrowLeft size={14}/> Back to project
      </Link>

      {/* Header */}
      <div className="mb-5">
        <div className="text-[11px] font-mono text-text-muted">
          {project.project_code} · {project.project_name}
        </div>
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2 mt-0.5">
          <FileBox size={20} className="text-accent"/>
          Deliverables
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          Master templates for the field, and project-specific filled versions
          uploaded back. Carrier:{" "}
          <span className="font-mono text-text">{project.carrier?.carrier_code ?? "generic"}</span>.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit overflow-x-auto">
        {categories.map(cat => (
          <button
            key={cat.category_id}
            type="button"
            onClick={() => setActiveTab(cat.code)}
            className={`px-3 py-1.5 rounded text-[12px] transition-colors whitespace-nowrap ${
              activeTab === cat.code
                ? "bg-accent/15 text-accent border border-accent/30"
                : "text-text-muted hover:text-text border border-transparent"
            }`}
          >
            {cat.display_name}
          </button>
        ))}
      </div>

      {/* Lines */}
      {loadingLines ? (
        <div className="p-12 flex justify-center"><Spinner size={20}/></div>
      ) : lines.length === 0 ? (
        <Card>
          <EmptyState
            title="No lines configured for this category yet"
            description={canManage
              ? "Add lines via SQL seed (see migration 047 pattern). Future: admin UI for line management."
              : "Ask your manager to set up the deliverable lines for this category."}
            icon={Layers}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {lines.map(line => (
            <LineCard
              key={line.line_id}
              line={line}
              uploads={uploadsByLine.get(line.line_id) ?? []}
              canManage={canManage}
              projectId={projectId!}
              onUploadComplete={() => {
                refetchUploads();
                qc.invalidateQueries({ queryKey: ["deliv_lines"] });
              }}
              onMasterUploaded={() => {
                qc.invalidateQueries({ queryKey: ["deliv_lines"] });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// LineCard — one card per deliverable line
// -----------------------------------------------------------------------------
function LineCard({
  line, uploads, canManage, projectId, onUploadComplete, onMasterUploaded,
}: {
  line:             Line;
  uploads:          Upload[];
  canManage:        boolean;
  projectId:        string;
  onUploadComplete: () => void;
  onMasterUploaded: () => void;
}) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const masterInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState<{ phase: string; pct: number } | null>(null);
  const [uploadingMaster, setUploadingMaster] = useState<{ phase: string; pct: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleProjectUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    for (const file of Array.from(files)) {
      try {
        await uploadProjectDeliverable({
          projectId,
          lineId: line.line_id,
          file,
          onProgress: (phase, pct) => setUploading({ phase, pct }),
        });
      } catch (e: any) {
        setError(e?.message ?? "Upload failed");
        setUploading(null);
        return;
      }
    }
    setUploading(null);
    onUploadComplete();
  }

  async function handleMasterUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    const file = files[0];   // master is single
    try {
      await uploadMasterTemplate({
        lineId: line.line_id,
        file,
        onProgress: (phase, pct) => setUploadingMaster({ phase, pct }),
      });
    } catch (e: any) {
      setError(e?.message ?? "Master upload failed");
      setUploadingMaster(null);
      return;
    }
    setUploadingMaster(null);
    onMasterUploaded();
  }

  async function handleDownloadMaster() {
    if (!line.master_storage_path) return;
    const url = await getDeliverableSignedUrl(line.master_storage_path, `attachment; filename="${line.master_file_name ?? "master"}"`);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleDownloadUpload(upload: Upload) {
    const url = await getDeliverableSignedUrl(upload.storage_path, `attachment; filename="${upload.file_name}"`);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleDeleteUpload(upload: Upload) {
    if (!window.confirm(`Delete "${upload.file_name}"? This cannot be undone.`)) return;
    try {
      await deleteDeliverableUpload(upload.upload_id);
    } catch (e: any) {
      window.alert(`Delete failed: ${e?.message ?? "unknown"}`);
      return;
    }
    onUploadComplete();
  }

  return (
    <Card>
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[15px] font-semibold text-text">{line.display_name}</h3>
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">{line.code}</span>
              {line.carrier_id && (
                <span className="text-[9px] font-mono uppercase tracking-wider2 text-accent px-1.5 py-0.5 rounded bg-accent/10 border border-accent/30">
                  carrier
                </span>
              )}
            </div>
            {line.description && (
              <p className="text-[12px] text-text-muted mt-1 leading-snug">{line.description}</p>
            )}
          </div>
          <div className="text-[11px] font-mono text-text-muted flex-shrink-0">
            {uploads.length} upload{uploads.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* Master template */}
        {line.has_master && (
          <div className="mb-3 p-3 rounded bg-ink border border-line">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-accent flex-shrink-0"/>
                <div className="min-w-0">
                  <div className="text-[12px] text-text font-medium">Master template</div>
                  <div className="text-[11px] font-mono text-text-muted">
                    {line.master_storage_path
                      ? <>
                          {line.master_file_name ?? "template"}
                          {line.master_version_label && <> · <span className="text-accent">{line.master_version_label}</span></>}
                          {line.master_uploaded_at && <> · updated {formatDistanceToNow(parseISO(line.master_uploaded_at), { addSuffix: true })}</>}
                        </>
                      : <span className="italic text-text-dim">No master uploaded yet</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {line.master_storage_path && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Download}
                    onClick={handleDownloadMaster}
                  >
                    Download
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Upload}
                      onClick={() => masterInputRef.current?.click()}
                      disabled={uploadingMaster !== null}
                    >
                      {uploadingMaster
                        ? `${uploadingMaster.phase}…`
                        : line.master_storage_path ? "Replace" : "Upload master"}
                    </Button>
                    <input
                      ref={masterInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => { handleMasterUpload(e.target.files); e.target.value = ""; }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Project uploads */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">
              Project uploads
            </div>
            <div>
              <Button
                variant="primary"
                size="sm"
                icon={Upload}
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploading !== null}
              >
                {uploading
                  ? `${uploading.phase}…`
                  : "Upload new"}
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { handleProjectUpload(e.target.files); e.target.value = ""; }}
              />
            </div>
          </div>

          {uploads.length === 0 ? (
            <div className="text-[12px] text-text-muted italic">
              No uploads yet. Tap "Upload new" to add the filled version.
            </div>
          ) : (
            <div className="space-y-1.5">
              {uploads.map(u => (
                <UploadRow
                  key={u.upload_id}
                  upload={u}
                  canDelete={canManage /* RLS handles capturer-self too */}
                  onDownload={() => handleDownloadUpload(u)}
                  onDelete={() => handleDeleteUpload(u)}
                />
              ))}
            </div>
          )}

          {error && (
            <div className="mt-2 text-[11px] text-danger flex items-start gap-1.5">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/>
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
function UploadRow({
  upload, canDelete, onDownload, onDelete,
}: {
  upload:     Upload;
  canDelete:  boolean;
  onDownload: () => void;
  onDelete:   () => void;
}) {
  function fmtBytes(n: number | null): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }
  return (
    <div className="flex items-center gap-3 p-2.5 rounded bg-ink border border-line">
      <FileText size={14} className="text-text-muted flex-shrink-0"/>
      <button
        onClick={onDownload}
        type="button"
        className="flex-1 min-w-0 text-left text-[12px] text-text hover:text-accent truncate"
      >
        {upload.file_name}
      </button>
      {upload.version_label && (
        <span className="text-[10px] font-mono text-accent bg-accent/10 border border-accent/30 px-1.5 py-0.5 rounded">
          {upload.version_label}
        </span>
      )}
      <Pill label={upload.status} tone={STATUS_TONE[upload.status]} small />
      <span className="text-[10px] text-text-muted font-mono flex-shrink-0">
        {fmtBytes(upload.file_size_bytes)}
      </span>
      <span className="text-[10px] text-text-dim font-mono flex-shrink-0 hidden sm:inline">
        {format(parseISO(upload.uploaded_at), "dd MMM")}
      </span>
      {canDelete && (
        <button
          onClick={onDelete}
          type="button"
          className="text-text-muted hover:text-danger flex-shrink-0 p-1"
          title="Delete"
        >
          <Trash2 size={12}/>
        </button>
      )}
    </div>
  );
}
